import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { listDiagnosticCompanies } from "@/api/listDiagnosticCompanies";
import { fetchCompanyDiagnostics } from "@/api/fetchCompanyDiagnostics";
import {
  CompanyDetailResponse,
  DiagnosticCompanyRow,
} from "../types/diagnostic";

interface DiagnosticCompaniesState {
  list: DiagnosticCompanyRow[];
  listLoading: boolean;
  listError: string | null;

  detail: CompanyDetailResponse | null;
  detailLoading: boolean;
  detailError: string | null;
}

const initialState: DiagnosticCompaniesState = {
  list: [],
  listLoading: false,
  listError: null,
  detail: null,
  detailLoading: false,
  detailError: null,
};

const errorMessage = (e: unknown, fallback: string): string => {
  if (typeof e === "object" && e !== null && "response" in e) {
    const r = (
      e as {
        response?: {
          data?: {
            detail?: string;
            message?: string;
            errors?: { detail?: string };
          };
        };
      }
    ).response;
    return (
      r?.data?.errors?.detail ?? r?.data?.detail ?? r?.data?.message ?? fallback
    );
  }
  return fallback;
};

export const fetchCompaniesListThunk = createAsyncThunk<
  DiagnosticCompanyRow[],
  void,
  { rejectValue: string }
>("diagnosticCompanies/list", async (_, { rejectWithValue }) => {
  try {
    return await listDiagnosticCompanies();
  } catch (e: unknown) {
    return rejectWithValue(errorMessage(e, "Failed to load companies."));
  }
});

export const fetchCompanyDetailThunk = createAsyncThunk<
  CompanyDetailResponse,
  number,
  { rejectValue: string }
>("diagnosticCompanies/detail", async (userId, { rejectWithValue }) => {
  try {
    return await fetchCompanyDiagnostics(userId);
  } catch (e: unknown) {
    return rejectWithValue(errorMessage(e, "Failed to load company."));
  }
});

const slice = createSlice({
  name: "diagnosticCompanies",
  initialState,
  reducers: {
    clearCompanyDetail: (state) => {
      state.detail = null;
      state.detailError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompaniesListThunk.pending, (s) => {
        s.listLoading = true;
        s.listError = null;
      })
      .addCase(fetchCompaniesListThunk.fulfilled, (s, a) => {
        s.listLoading = false;
        s.list = a.payload;
      })
      .addCase(fetchCompaniesListThunk.rejected, (s, a) => {
        s.listLoading = false;
        s.listError = a.payload ?? "Failed.";
      })

      .addCase(fetchCompanyDetailThunk.pending, (s) => {
        s.detailLoading = true;
        s.detailError = null;
      })
      .addCase(fetchCompanyDetailThunk.fulfilled, (s, a) => {
        s.detailLoading = false;
        s.detail = a.payload;
      })
      .addCase(fetchCompanyDetailThunk.rejected, (s, a) => {
        s.detailLoading = false;
        s.detailError = a.payload ?? "Failed.";
      });
  },
});

export const { clearCompanyDetail } = slice.actions;
export default slice.reducer;
